import { motion } from "framer-motion";
import { Dispatch, FC, SetStateAction, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { e2p } from "../../utils";

type OTPInputProps = {
  length?: number;
  onComplete?: (otp: string) => void;
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
  isError: boolean;
  setIsError: Dispatch<SetStateAction<boolean>>;
  otp: string[];
  setOtp: Dispatch<SetStateAction<string[]>>;
  className?: string;
};

const OTPInputV2: FC<OTPInputProps> = ({
  length = 4,
  onComplete,
  setIsDisabled,
  isError,
  setIsError,
  otp,
  setOtp,
  className,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const firstEmptyIndex = otp.findIndex((digit) => digit === "");

    if (firstEmptyIndex !== -1 && inputsRef.current[firstEmptyIndex]) {
      inputsRef.current[firstEmptyIndex]?.focus();
    } else {
      inputsRef.current[length - 1]?.blur();
    }
  }, [otp]);

  const handleChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }

    if (otp.filter(Boolean).length === 4 || otp.filter(Boolean).length === 5) {
      setIsDisabled(true);
    }
  };

  const handleFocus = (index: number) => {
    if (isError) setIsError(false);

    const firstEmptyIndex = otp.findIndex((digit) => digit === "");
    if (firstEmptyIndex !== -1 && index > firstEmptyIndex) {
      inputsRef.current[firstEmptyIndex]?.focus();
    }
  };

  useEffect(() => {
    if (otp.filter(Boolean).length === length) {
      setIsDisabled(false);
      onComplete?.(otp.join(""));
    }
  }, [otp]);

  return (
    <div className="flex justify-between gap-3 sm:gap-5" dir="ltr">
      {otp.map((digit, idx) => (
        <motion.input
          key={idx}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={e2p(digit)}
          onChange={(e) => handleChange(e.target.value, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onFocus={() => handleFocus(idx)}
          ref={(el: HTMLInputElement | null) => {
            inputsRef.current[idx] = el;
          }}
          className={twMerge(
            "!size-9 xxxs:!size-12 bg-transparent text-black text-center text-xl font-semibold border-2 rounded focus:outline-none transition-all",
            isError
              ? "border-error text-erborder-error"
              : "border-gray-300 focus:border-primary",
            className
          )}
          animate={
            isError
              ? { x: [0, -10, 10, -10, 10, 0, -10, 10, -10, 10, 0] }
              : { opacity: 1 }
          }
          initial={{ opacity: 1 }}
          transition={{
            duration: isError ? 0.7 : 0.2,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

export default OTPInputV2;
